import { useEffect, useState, useMemo } from 'react'
import { subscribeToListings, filterListings, sortListings } from '@/services/api/listings'
import type { Listing } from '@/types/listing'
import type { ListingFilters, SortOption } from '@/types/filters'

/** A listen that never delivers a first snapshot should degrade to a message,
 *  not an indefinite skeleton. */
const LISTEN_TIMEOUT_MS = 15000

interface UseListingsOptions {
  category?: string
  sellerId?: string
  enabled?: boolean
  allStatuses?: boolean
}

interface UseListingsReturn {
  listings: Listing[]
  isLoading: boolean
  error: string | null
}

// Hook to fetch all active listings with real-time updates
export function useListings(options?: UseListingsOptions): UseListingsReturn {
  const [listings, setListings] = useState<Listing[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const { category, sellerId, enabled = true, allStatuses = false } = options || {}

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    let settled = false

    const timer = setTimeout(() => {
      if (settled) return
      console.error('Listings subscription timed out after', LISTEN_TIMEOUT_MS, 'ms')
      setError('Timed out loading listings. Check your connection and try again.')
      setIsLoading(false)
    }, LISTEN_TIMEOUT_MS)

    const unsubscribe = subscribeToListings(
      (newListings) => {
        settled = true
        setListings(newListings)
        setError(null)
        setIsLoading(false)
      },
      { category, sellerId, allStatuses },
      (err) => {
        settled = true
        setError(err.message || 'Could not load listings.')
        setIsLoading(false)
      }
    )

    return () => {
      clearTimeout(timer)
      unsubscribe()
    }
  }, [category, sellerId, enabled, allStatuses])

  return { listings, isLoading, error }
}

// Hook to fetch and filter listings with search/filter support
export function useFilteredListings(
  filters: Partial<ListingFilters>,
  sortBy: SortOption = 'newest'
) {
  const { listings, isLoading, error } = useListings()

  const filteredListings = useMemo(() => {
    if (!listings.length) return []

    // Convert filter strings to appropriate types
    const numericFilters = {
      query: filters.query || undefined,
      category: filters.category || undefined,
      group: filters.group || undefined,
      location: filters.location || undefined,
      priceMin: filters.priceMin ? parseFloat(filters.priceMin) : undefined,
      priceMax: filters.priceMax ? parseFloat(filters.priceMax) : undefined,
      condition: filters.condition || undefined,
      saleType: filters.saleType || undefined,
      make: filters.make || undefined,
      model: filters.model || undefined,
      yearMin: filters.yearMin ? parseInt(filters.yearMin) : undefined,
      yearMax: filters.yearMax ? parseInt(filters.yearMax) : undefined,
      transmission: filters.transmission || undefined,
      bodyType: filters.bodyType || undefined,
    }

    const filtered = filterListings(listings, numericFilters)

    // Auto-switch to relevance sorting when a search query is active and
    // the user hasn't explicitly chosen a different sort order
    const effectiveSort = (filters.query && sortBy === 'newest') ? 'relevance' : sortBy
    return sortListings(filtered, effectiveSort, filters.query || undefined)
  }, [listings, filters, sortBy])

  return {
    listings: filteredListings,
    totalCount: listings.length,
    filteredCount: filteredListings.length,
    isLoading,
    error,
  }
}

// Hook for user's own listings (all statuses so they can manage drafts, sold, etc.)
export function useMyListings(userId: string | undefined) {
  return useListings({
    sellerId: userId,
    enabled: !!userId,
    allStatuses: true,
  })
}

// Hook for recent listings (homepage)
export function useRecentListings(limitCount: number = 8, options?: UseListingsOptions) {
  const { listings, isLoading, error } = useListings(options)

  const recentListings = useMemo(() => {
    // Sort by createdAt descending and take the first N
    const sorted = [...listings].sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0
      const bTime = b.createdAt?.toMillis?.() || 0
      return bTime - aTime
    })
    return sorted.slice(0, limitCount)
  }, [listings, limitCount])

  return { listings: recentListings, isLoading, error }
}
